import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Alert,
  Spin,
  Tag,
} from 'antd';
import { LinkOutlined, CloseOutlined, LoadingOutlined, ReloadOutlined } from '@ant-design/icons';
import { apiService } from '../../services/api';
import { BoxAuthStatus } from '../../types';

interface BoxIntegrationProps {
  className?: string;
}

export const BoxIntegration: React.FC<BoxIntegrationProps> = ({ className }) => {
  const [status, setStatus] = useState<BoxAuthStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadStatus = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await apiService.getBoxStatus();
      
      // Handle both old and new response formats
      const isConnected = response.connected ?? response.isConnected ?? false;
      const userEmail = response.userInfo?.login ?? response.userInfo?.name ?? response.userEmail;
      const expiresAt = response.expiresAt ? (typeof response.expiresAt === 'number' ? new Date(response.expiresAt).toISOString() : response.expiresAt) : undefined;
      
      setStatus({
        isConnected,
        userEmail,
        expiresAt
      });
    } catch (err) {
      console.error('Failed to load Box status:', err);
      setError(err instanceof Error ? err.message : 'Failed to load Box status');
      setStatus({ isConnected: false });
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      setIsConnecting(true);
      setError(null);
      
      const authResponse = await apiService.initiateBoxAuth();
      
      // Open the Box OAuth URL in a new window
      const authWindow = window.open(
        authResponse.authUrl,
        'box-auth',
        'width=600,height=700,scrollbars=yes,resizable=yes'
      );

      if (!authWindow) {
        throw new Error('Failed to open authentication window. Please check your popup blocker settings.');
      }

      // Poll for the window to close (indicating auth completion)
      const pollTimer = setInterval(() => {
        if (authWindow.closed) {
          clearInterval(pollTimer);
          // Reload status after auth completes
          setTimeout(() => {
            loadStatus();
            setIsConnecting(false);
          }, 1000);
        }
      }, 1000);

      // Clean up timer after 5 minutes
      setTimeout(() => {
        clearInterval(pollTimer);
        if (!authWindow.closed) {
          authWindow.close();
        }
        setIsConnecting(false);
      }, 5 * 60 * 1000);

    } catch (err) {
      console.error('Failed to initiate Box authentication:', err);
      setError(err instanceof Error ? err.message : 'Failed to connect to Box');
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      setIsDisconnecting(true);
      setError(null);
      
      await apiService.disconnectBox();
      setStatus({ isConnected: false });
      
    } catch (err) {
      console.error('Failed to disconnect Box:', err);
      setError(err instanceof Error ? err.message : 'Failed to disconnect Box');
    } finally {
      setIsDisconnecting(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  if (isLoading) {
    return (
      <Card className={className}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <Spin indicator={<LoadingOutlined spin />} />
          <span>Loading Box integration status...</span>
        </div>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h5 style={{ margin: 0 }}>Box Integration</h5>
          <Tag color={status?.isConnected ? 'green' : 'default'}>
            {status?.isConnected ? 'Connected' : 'Disconnected'}
          </Tag>
        </div>

        <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
          Connect your Box account to access files, folders, and documents through AI chat.
        </p>

        {status?.isConnected && status.userEmail && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <div style={{ fontSize: '0.875rem' }}>
              <strong>Account:</strong> {status.userEmail}
            </div>
            {status.expiresAt && (
              <div style={{ fontSize: '0.875rem' }}>
                <strong>Token expires:</strong> {new Date(status.expiresAt).toLocaleString()}
              </div>
            )}
          </div>
        )}

        {error && (
          <Alert
            type="error"
            message="Integration Error"
            description={error}
            showIcon
          />
        )}

        <div style={{ display: 'flex', gap: '1rem' }}>
          {!status?.isConnected ? (
            <Button
              type="primary"
              icon={<LinkOutlined />}
              onClick={handleConnect}
              loading={isConnecting}
            >
              Connect Box
            </Button>
          ) : (
            <Button
              danger
              icon={<CloseOutlined />}
              onClick={handleDisconnect}
              loading={isDisconnecting}
            >
              Disconnect
            </Button>
          )}

          <Button
            type="default"
            icon={<ReloadOutlined />}
            onClick={loadStatus}
            loading={isLoading}
            size="small"
          >
            Refresh Status
          </Button>
        </div>

        {status?.isConnected && (
          <div style={{ 
            fontSize: '0.75rem', 
            color: 'var(--color-text-secondary)',
            fontStyle: 'italic',
            marginTop: '0.5rem'
          }}>
            Box integration provides AI chat access to your files, folders, search, upload, and download capabilities.
          </div>
        )}
      </div>
    </Card>
  );
};

export default BoxIntegration;